import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { ArrowRight } from "lucide-react";
import { LazyLottie } from "@/components/common/LazyLottie";
import { homepageHero, homepageRoomLines } from "@/data/homepageContent";

/**
 * Eager, framer-motion-FREE render of the home hero (above-the-fold). This is rendered
 * straight from the entry bundle (react + ui only) and stays mounted while the rest of
 * the page (ModernHomePageThree body) streams in lazily beneath it. Because the hero —
 * and its subtitle, the Largest Contentful Paint element — is a stable element painted
 * without waiting for the framer-motion chunk or the lazy page chunk, mobile LCP drops
 * from "whole-bundle render delay" to "entry render". The KineticHeadline / Reveal
 * entrances are reproduced with CSS keyframes (see index.css) so the look is preserved.
 *
 * Keep this file dependency-light: no framer-motion, no gsap.
 */

const HERO_LOTTIE = "/homepage/481759c0-116c-11ee-b865-67dff3c72813.lottie";

type Mode = "light" | "dark" | "material";

// The subset of ModernHomePageThree's palette the hero reads. Kept in sync with PALETTES
// there.
const HERO_VARS: Record<Mode, CSSProperties> = {
  light: {
    "--h3-bg": "#FFFFFF",
    "--h3-fg": "rgb(21,18,13)",
    "--h3-fg85": "rgba(21,18,13,0.85)",
    "--h3-fg60": "rgba(21,18,13,0.6)",
    "--h3-accent": "#0A53B3",
    "--h3-glow": "rgba(10,83,179,0.12)",
    "--h3-line": "rgba(21,18,13,0.16)",
    "--h3-line-strong": "rgba(21,18,13,0.3)",
  } as CSSProperties,
  dark: {
    "--h3-bg": "#0A0E14",
    "--h3-fg": "rgb(233,238,245)",
    "--h3-fg85": "rgba(233,238,245,0.85)",
    "--h3-fg60": "rgba(233,238,245,0.6)",
    "--h3-accent": "#5AA8FF",
    "--h3-glow": "rgba(90,168,255,0.12)",
    "--h3-line": "rgba(233,238,245,0.16)",
    "--h3-line-strong": "rgba(233,238,245,0.3)",
  } as CSSProperties,
  material: {
    "--h3-bg": "#0C0F13",
    "--h3-fg": "rgb(226,232,240)",
    "--h3-fg85": "rgba(226,232,240,0.85)",
    "--h3-fg60": "rgba(226,232,240,0.6)",
    "--h3-accent": "#6BB1FF",
    "--h3-glow": "rgba(107,177,255,0.12)",
    "--h3-line": "rgba(226,232,240,0.16)",
    "--h3-line-strong": "rgba(226,232,240,0.3)",
  } as CSSProperties,
};

function readMode(): Mode {
  if (typeof document === "undefined") return "light";
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "material-dark") return "material";
  if (root.classList.contains("dark")) return "dark";
  return "light";
}

function trackHomepageEvent(event: string, detail?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("choice-wealth-track", { detail: { event, ...detail } }));
  const dataLayer = (window as Window & { dataLayer?: Array<Record<string, unknown>> }).dataLayer;
  dataLayer?.push({ event, ...detail });
}

const HEADLINE_LINES = [
  <>Where</>,
  <>wealth</>,
  <span className="text-[color:var(--h3-accent)]">comes home.</span>,
];

export function HomeHero() {
  const [mode, setMode] = useState<Mode>(() => readMode());
  useEffect(() => {
    const update = () => setMode(readMode());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });
    return () => observer.disconnect();
  }, []);
  const themeVars = HERO_VARS[mode];
  const ticker = useMemo(() => homepageRoomLines.map((room) => room.name), []);

  return (
    <div className="home-minimal relative overflow-hidden" style={{ ...themeVars, background: "var(--h3-bg)", color: "var(--h3-fg)" }}>
      <div className="relative z-10 xl:px-32 min-[1600px]:px-0">
        <section className="relative pt-10 md:pt-14">
          <div id="h3-hero" className="mx-auto w-full max-w-[1320px] scroll-mt-24 px-6">
            <div className="flex items-center justify-between border-b pb-4 text-[0.7rem] font-semibold uppercase tracking-[0.3em]" style={{ borderColor: "var(--h3-line-strong)", color: "var(--h3-fg60)" }}>
              <span>{homepageHero.eyebrow}</span>
              <span className="hidden sm:inline">Choice Wealth, 2026</span>
            </div>

            <div className="relative grid grid-cols-12 gap-y-8 pt-6 md:pt-8">
              <div className="col-span-12 xl:col-span-8">
                <h1 className="font-heading text-[clamp(2.4rem,8.5vw,7.5rem)] font-black leading-[0.86] tracking-[-0.05em]">
                  {HEADLINE_LINES.map((line, index) => (
                    <span key={index} className="block overflow-hidden pb-[0.06em]">
                      <span
                        className="block motion-safe:animate-[hero-line-rise_0.85s_cubic-bezier(0.22,1,0.36,1)_both]"
                        style={{ animationDelay: `${index * 0.12}s` }}
                      >
                        {line}
                      </span>
                    </span>
                  ))}
                </h1>
              </div>

              <div className="col-span-12 self-end xl:col-span-4">
                <div className="relative ml-auto aspect-square w-full max-w-[15rem] md:max-w-[17rem] xl:max-w-[20rem] xl:translate-y-6 motion-safe:animate-[hero-fade-up_0.9s_cubic-bezier(0.22,1,0.36,1)_0.3s_both]">
                  <div
                    aria-hidden
                    className="absolute inset-[8%] rounded-full blur-3xl"
                    style={{ background: "radial-gradient(circle, var(--h3-glow), transparent 70%)" }}
                  />
                  <div
                    className="relative flex h-full w-full items-center justify-center"
                    style={{ transform: "scale(1.4)", transformOrigin: "center" }}
                  >
                    <LazyLottie src={HERO_LOTTIE} loop autoplay />
                  </div>
                </div>
              </div>

              <div className="col-span-12 grid grid-cols-12 items-end gap-y-6 border-t pt-7 xl:col-span-12" style={{ borderColor: "var(--h3-line)" }}>
                <div className="col-span-12 md:col-span-7">
                  <p className="max-w-xl text-2xl font-medium leading-9 tracking-tight md:text-3xl" style={{ color: "var(--h3-fg)" }}>
                    {homepageHero.subtitle}
                  </p>
                  <p className="mt-4 max-w-md text-sm leading-7" style={{ color: "var(--h3-fg60)" }}>{homepageHero.microcopy}</p>
                </div>
                <div className="col-span-12 flex flex-col gap-3 md:col-span-5 md:items-end motion-safe:animate-[hero-fade-up_0.9s_cubic-bezier(0.22,1,0.36,1)_0.5s_both]">
                  <a
                    href="#h3-start-selector"
                    onClick={() => trackHomepageEvent("hero_cta_click", { variant: "home-3" })}
                    className="group inline-flex w-full items-center justify-center gap-2 px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-colors sm:w-[17rem]"
                    style={{ background: "var(--h3-fg)", color: "var(--h3-bg)" }}
                  >
                    {homepageHero.primaryCta}
                    <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </a>
                  <a
                    href="#h3-rhythm-start"
                    onClick={() => trackHomepageEvent("secondary_cta_click", { variant: "home-3" })}
                    className="inline-flex w-full items-center justify-center gap-2 border px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-colors sm:w-[17rem]"
                    style={{ borderColor: "var(--h3-line-strong)", color: "var(--h3-fg)" }}
                  >
                    {homepageHero.secondaryCta}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-12 overflow-hidden border-y py-4" style={{ borderColor: "var(--h3-line-strong)" }}>
              <div className="flex w-max animate-[h2-marquee_32s_linear_infinite] items-center motion-reduce:animate-none">
                {[...ticker, ...ticker].map((item, index) => (
                  <span key={`${item}-${index}`} className="flex shrink-0 items-center gap-8 pr-8 font-heading text-2xl font-black uppercase tracking-tight md:text-3xl" style={{ color: "var(--h3-fg85)" }}>
                    {item}
                    <span className="text-[color:var(--h3-accent)]">✦</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
